import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import moment from 'moment';
import { Subscription, forkJoin, interval } from 'rxjs';
import { isNil as _isNil } from 'lodash';

import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxSwitchComponent } from 'devextreme-angular/ui/switch';
import { DxSparklineComponent } from 'devextreme-angular/ui/sparkline';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import {
    DxiColumnComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoMasterDetailComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoScrollingComponent,
    DxoSearchPanelComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';

import {
    MessageType,
    NotificationService,
    UtilService,
} from 'src/app/shared/services';
import { FormatNumberPipe } from 'src/app/shared/pipes/format-number.pipe';
import {
    CrawlRun,
    CrawlState,
    CrawlerCardVm,
    CrawlerHealth,
    HealthLevel,
    IssueKind,
    IssueVm,
    KeyValue,
    RunDetail,
} from './models/crawler-mon.model';
import { CrawlerMonService } from './services/crawler-mon.service';
import {
    NIGHTLY_CRAWLERS,
    buildHealth,
    cleanMessage,
    diagnosticFor,
    levelSeverity,
    nextScheduledSlot,
} from './services/crawler-diagnostics';

interface OverallState {
    level: HealthLevel;
    icon: string;
    title: string;
    detail: string;
}

const AUTO_REFRESH_MS = 5 * 60 * 1000;

const LEVEL_ICONS: Record<HealthLevel, string> = {
    ok: '✅',
    degraded: '🟡',
    failed: '🔴',
    overdue: '⏰',
    unknown: '❔',
};

const KIND_LABELS: Record<IssueKind, string> = {
    structure: 'page structure',
    auth: 'login / session',
    transient: 'transient',
    'data-gap': 'data gap',
    infra: 'infrastructure',
    unknown: 'unclassified',
};

@Component({
    selector: 'app-crawler-mon',
    templateUrl: './crawler-mon.component.html',
    styleUrls: ['./crawler-mon.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgTemplateOutlet,
        DxButtonComponent,
        DxSelectBoxComponent,
        DxSwitchComponent,
        DxSparklineComponent,
        DxLoadPanelComponent,
        DxDataGridComponent,
        DxiColumnComponent,
        DxoFilterRowComponent,
        DxoHeaderFilterComponent,
        DxoMasterDetailComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxoScrollingComponent,
        DxoSearchPanelComponent,
        DxTemplateDirective,
        FormatNumberPipe,
    ],
})
export class CrawlerMonComponent implements OnInit {
    private crawlerMonService = inject(CrawlerMonService);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);
    private destroyRef = inject(DestroyRef);

    readonly runLimitOptions = [50, 100, 200, 500];

    runLimit = 200;
    autoRefresh = false;
    isLoading = false;

    runs: CrawlRun[] = [];
    cards: CrawlerCardVm[] = [];
    /** Issues across all crawlers, worst and still-active first. */
    issues: IssueVm[] = [];
    overall: OverallState = {
        level: 'unknown',
        icon: LEVEL_ICONS.unknown,
        title: 'Loading…',
        detail: '',
    };
    lastRefreshed: string | null = null;

    private autoRefreshSubscription: Subscription | null = null;

    /**
     * Expanded-row payloads, keyed by the run document itself.
     *
     * The grid's master-detail template runs on every change-detection tick —
     * and DevExtreme ticks the zone on wheel and click — so shaping a run's
     * counts/metrics/errors inline would re-allocate, and re-run the ANSI strip
     * over up to 25 messages, dozens of times a second. Caching keeps the
     * template call cheap and hands back a stable reference, while staying lazy
     * so collapsed rows cost nothing. A WeakMap drops entries with the runs.
     */
    private readonly runDetails = new WeakMap<CrawlRun, RunDetail>();

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        if (this.isLoading) {
            return;
        }
        this.isLoading = true;
        forkJoin({
            runs: this.crawlerMonService.getRuns(this.runLimit),
            states: this.crawlerMonService.getStates(),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ runs, states }) => {
                    this.applyData(runs ?? [], states ?? []);
                    this.isLoading = false;
                },
                error: (error: unknown) => {
                    this.isLoading = false;
                    const message = this.utilService.errorToString(error);
                    this.overall = {
                        level: 'unknown',
                        icon: LEVEL_ICONS.unknown,
                        title: 'Could not read crawl_runs',
                        detail: `The monitor itself failed to load, so nothing below is current. ${message}`,
                    };
                    this.notificationService.pushMessage({
                        message: `Loading crawler runs failed: ${message}`,
                        messageType: MessageType.error,
                    });
                },
            });
    }

    private applyData(runs: CrawlRun[], states: CrawlState[]): void {
        const now = moment();
        // The Lambda honours `sort`, but re-sorting locally keeps the page
        // correct even if that ever stops being true — `buildHealth` reads
        // runs[0] as "latest". `startedAt` is ISO-8601 in UTC, so a string
        // compare orders it exactly without parsing 500 dates.
        this.runs = [...runs].sort((a, b) =>
            b.startedAt.localeCompare(a.startedAt),
        );

        const healths = NIGHTLY_CRAWLERS.map((crawler) =>
            buildHealth(
                crawler,
                this.runs,
                states.find((state) => state._id === crawler)
                    ?.completedThrough ?? null,
                now,
            ),
        ).sort((a, b) => levelSeverity(b.level) - levelSeverity(a.level));

        this.cards = healths.map((health) => this.toCardVm(health, now));
        this.issues = healths
            .flatMap((health) => health.issues)
            .sort(
                (a, b) =>
                    Number(b.active) - Number(a.active) || b.total - a.total,
            )
            .map((issue) => {
                const diagnostic = diagnosticFor(issue.category);
                return {
                    ...issue,
                    kindText: KIND_LABELS[issue.kind],
                    meaning: diagnostic.meaning,
                    action: diagnostic.action,
                    lastSeenAgo: this.fromNow(issue.lastSeen),
                };
            });

        this.overall = this.buildOverall(
            nextScheduledSlot(now).format('D MMM, HH:mm [ICT]'),
        );
        this.lastRefreshed = now.format('D MMM YYYY HH:mm:ss');
    }

    /** Resolve every string the card renders, once per load. */
    private toCardVm(health: CrawlerHealth, now: moment.Moment): CrawlerCardVm {
        const lastRun = health.lastRun;
        return {
            ...health,
            icon: LEVEL_ICONS[health.level],
            statusText:
                health.level === 'overdue' ? 'did not run' : health.level,
            headline: this.buildHeadline(health, now),
            lastRunAgo: this.fromNow(lastRun?.startedAt),
            lastRunAt: _isNil(lastRun)
                ? '—'
                : moment(lastRun.startedAt).format('D MMM YYYY HH:mm'),
            lastOkRunAgo: this.fromNow(health.lastOkRun?.startedAt),
            durationText: _isNil(lastRun)
                ? '—'
                : this.humanDuration(lastRun.durationMs),
            versionText: lastRun?.version || '—',
            deltaText:
                health.deltaPct === null
                    ? 'no baseline yet'
                    : `${health.deltaPct >= 0 ? '+' : ''}${health.deltaPct.toFixed(1)}% vs median of last runs`,
            errorChips: _isNil(lastRun) ? [] : this.runDetail(lastRun).errors,
        };
    }

    private buildHeadline(health: CrawlerHealth, now: moment.Moment): string {
        const metric = health.primaryMetricLabel.toLowerCase();
        switch (health.level) {
            case 'overdue':
                return health.lastRun
                    ? `No run since ${moment(health.lastRun.startedAt).from(now)} — the 02:00 job did not start`
                    : 'Never run — the 02:00 job has not started once';
            case 'failed':
                return 'Last run failed — data for this crawler is stale';
            case 'degraded':
                return health.volumeDrop
                    ? `Last run finished with problems and ${metric} dropped sharply`
                    : 'Last run finished with problems';
            case 'ok':
                return health.volumeDrop
                    ? `Ran clean, but ${metric} dropped sharply versus recent runs`
                    : 'Healthy — last run completed clean';
            default:
                return 'No run history found';
        }
    }

    private buildOverall(nextRunText: string): OverallState {
        if (this.cards.length === 0) {
            return {
                level: 'unknown',
                icon: LEVEL_ICONS.unknown,
                title: 'No run history',
                detail: 'crawl_runs returned nothing for the nightly crawlers.',
            };
        }

        const broken = this.cards.filter(
            (card) => card.level === 'failed' || card.level === 'overdue',
        );
        const overdue = broken.filter((card) => card.level === 'overdue');
        const degraded = this.cards.filter((card) => card.level === 'degraded');
        const structural = this.issues.filter(
            (issue) => issue.active && issue.kind === 'structure',
        );
        const dropped = this.cards.filter((card) => card.volumeDrop);

        if (broken.length > 0) {
            const level: HealthLevel =
                overdue.length > 0 ? 'overdue' : 'failed';
            const missed =
                overdue.length > 0
                    ? `${overdue.map((card) => card.crawler).join(', ')} did not run on schedule. `
                    : '';
            const cause =
                structural.length > 0
                    ? `${structural.length} active error ${structural.length > 1 ? 'categories point' : 'category points'} at a page-structure change.`
                    : 'Open the run detail below for the failing step.';
            return {
                level,
                icon: LEVEL_ICONS[level],
                title: `${broken.length} crawler${broken.length > 1 ? 's' : ''} need attention now`,
                detail: missed + cause,
            };
        }

        if (
            degraded.length > 0 ||
            structural.length > 0 ||
            dropped.length > 0
        ) {
            let detail: string;
            if (structural.length > 0) {
                detail =
                    'At least one active error looks like a page-structure change — fix it before it turns into a failed run.';
            } else if (degraded.length > 0) {
                detail = 'The last run finished but did not come back clean.';
            } else {
                detail = `Volume dropped sharply for ${dropped.map((card) => card.crawler).join(', ')} — check the run detail before trusting the data.`;
            }
            return {
                level: 'degraded',
                icon: LEVEL_ICONS.degraded,
                title: 'Running, but with problems',
                detail,
            };
        }

        const names = this.cards.map((card) => card.crawler).join(', ');
        return {
            level: 'ok',
            icon: LEVEL_ICONS.ok,
            title: 'All nightly crawlers healthy',
            detail: `${names} all completed clean. Next run expected ${nextRunText}.`,
        };
    }

    onRunLimitChanged(): void {
        this.load();
    }

    onAutoRefreshChanged(): void {
        this.autoRefreshSubscription?.unsubscribe();
        this.autoRefreshSubscription = null;
        if (this.autoRefresh) {
            this.autoRefreshSubscription = interval(AUTO_REFRESH_MS)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.load());
        }
    }

    // ---- template helpers -------------------------------------------------

    /** Cached per run — see `runDetails`. Safe to call from the template. */
    runDetail(run: CrawlRun): RunDetail {
        let detail = this.runDetails.get(run);
        if (!detail) {
            detail = {
                counts: toEntries(run.counts, (value) =>
                    Number(value).toLocaleString(),
                ),
                metrics: toEntries(run.metrics, String),
                errors: toEntries(run.errors, String),
                samples: (run.sampleErrors ?? []).map((sample) => ({
                    category: sample.category,
                    message: cleanMessage(sample.message),
                })),
            };
            this.runDetails.set(run, detail);
        }
        return detail;
    }

    humanDuration(ms: number): string {
        const totalSeconds = Math.round(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        return minutes > 0
            ? `${minutes}m ${totalSeconds % 60}s`
            : `${totalSeconds}s`;
    }

    private fromNow(value: string | null | undefined): string {
        return _isNil(value) ? '—' : moment(value).fromNow();
    }
}

function toEntries(
    source: Record<string, unknown> | undefined,
    format: (value: unknown) => string,
): KeyValue[] {
    return Object.entries(source ?? {}).map(([key, value]) => ({
        key,
        value: format(value),
    }));
}
